import { useCallback, useRef } from 'react';
import type { MappingCatalogRefreshSuspensionRef } from './useMappingParameterCatalogs';
import { useReadinessRevisionInvalidation } from './useReadinessRevisionInvalidation';

interface UseMappingReadinessCatalogRefreshInput {
  verifiedRevision: string | null;
  enabled: boolean;
  projectKey: string | null;
  refreshCatalog: () => Promise<unknown>;
  refreshSuspendedRef?: MappingCatalogRefreshSuspensionRef;
}

interface MappingReadinessCatalogRefreshResult {
  refreshReadinessCatalogs: () => void;
  revisionRefreshPending: boolean;
}

export function useMappingRepairCatalogSuspension(input: {
  attemptScope: string;
  projectKey: string | null;
  refreshCatalog: () => Promise<unknown>;
  refreshSuspendedRef: { current: MappingCatalogRefreshSuspensionRef['current'] };
}): (isRepairing: boolean) => void {
  const currentInputRef = useRef(input);
  const activeAttemptScopeRef = useRef<string | null>(null);
  currentInputRef.current = input;

  return useCallback((isRepairing: boolean) => {
    const current = currentInputRef.current;
    current.refreshSuspendedRef.current = {
      active: isRepairing,
      projectKey: isRepairing ? current.projectKey : null,
    };
    if (isRepairing) {
      activeAttemptScopeRef.current = current.attemptScope;
      return;
    }

    const requirementsChanged = activeAttemptScopeRef.current !== null &&
      activeAttemptScopeRef.current !== current.attemptScope;
    activeAttemptScopeRef.current = null;
    if (requirementsChanged) {
      void current.refreshCatalog().catch(() => {
        // The snapshot hook owns the fail-closed state and user-visible retry.
      });
    }
  }, []);
}

export function useStableMappingRepairSignature(
  signature: string,
  projectKey: string | null,
  refreshSuspendedRef: MappingCatalogRefreshSuspensionRef
): string {
  const stableSignatureRef = useRef(signature);
  if (
    !refreshSuspendedRef.current.active ||
    refreshSuspendedRef.current.projectKey !== projectKey
  ) {
    stableSignatureRef.current = signature;
  }
  return stableSignatureRef.current;
}

/**
 * Coordinates the request-scoped Revit evidence snapshot used by Mapping readiness.
 *
 * A verified setup revision can already exist when Mapping first mounts, so
 * the catalogs visible at that moment may describe the model before the wizard
 * mutation. Readiness must hold its repair gate until a forced refresh starts.
 */
export function useMappingReadinessCatalogRefresh({
  verifiedRevision,
  enabled,
  projectKey,
  refreshCatalog,
  refreshSuspendedRef,
}: UseMappingReadinessCatalogRefreshInput): MappingReadinessCatalogRefreshResult {
  const refreshReadinessCatalogs = useCallback(() => {
    if (
      refreshSuspendedRef?.current.active &&
      refreshSuspendedRef.current.projectKey === projectKey
    ) {
      return;
    }
    void refreshCatalog().catch(() => {
      // The snapshot hook owns the failure state surfaced by readiness.
    });
  }, [projectKey, refreshCatalog, refreshSuspendedRef]);
  const revisionRefreshPending = useReadinessRevisionInvalidation({
    verifiedRevision,
    enabled,
    invalidate: refreshReadinessCatalogs,
  });

  return {
    refreshReadinessCatalogs,
    revisionRefreshPending,
  };
}
